import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BannerSlider from '../components/BannerSlider/BannerSlider';
import LayoutWrapper from '../layout/LayoutWrapper';
import './pagestyles.css';

export const useMobileDetect = () => {
    const [width, setWidth] = useState(window.innerWidth);

    function handleWindowSizeChange() {
        setWidth(window.innerWidth);
    }

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    return { isMobile: width <= 768 };
};

function ScrollToAnchor() {
    const location = useLocation();
    const lastHash = useRef('');

    useEffect(() => {
        if (location.hash) {
            lastHash.current = location.hash.slice(1);
        }

        if (lastHash.current && document.getElementById(lastHash.current)) {
            setTimeout(() => {
                document
                    .getElementById(lastHash.current)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                lastHash.current = '';
            }, 100);
        }
    }, [location]);

    return null;
}

const h1Style = {
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'left',
    padding: '8px 2px',
    lineHeight: 1.3,
    color: '#1f1f1f'
};

const h2Style = {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'left',
    padding: '4px 0px',
    color: '#1f1f1f'
};

const p1Style = {
    fontSize: 14,
    fontWeight: 'normal',
    textAlign: 'left',
    color: '#585858',
    lineHeight: 1.7
};

const platformCapabilities = [
    {
        title: 'Inventory Management',
        description: 'Track stock levels, batch movement, and low-stock alerts so your store is always ready to sell.',
        icon: 'activity-icon.svg'
    },
    {
        title: 'Custom Storefront + Domain',
        description: 'Set up your own online store with your brand and domain, so your customers get a clean and professional experience every time they shop',
        icon: 'collaborate-icon.svg'
    },
    {
        title: 'Warehouse Management',
        description: 'Coordinate receiving, picking, packing, and dispatch with better visibility at every stage.',
        icon: 'integration-icon.svg'
    },
    {
        title: 'Team Management',
        description: 'Assign responsibilities, monitor task flow, and keep your operations team aligned in real time.',
        icon: 'manage-customers-icon.svg'
    },
    {
        title: 'Order Management & Tracking',
        description: 'Handle orders from checkout to delivery and let customers track progress with confidence.',
        icon: 'order-tracking-icon.svg'
    },
    {
        title: 'Expense Tracking + Attachments',
        description: 'Record business expenses and attach receipts or files to keep documentation audit-ready.',
        icon: 'pie-chart-icon.svg'
    },
    {
        title: 'Invoice Generation',
        description: 'Generate professional invoices in seconds to speed up billing and improve cashflow consistency.',
        icon: 'invoicing-icon.svg'
    }
];

const quickHighlights = [
    { title: 'All-in-One Operations', text: 'Handle your stock, orders, warehouse, and finances from one simple dashboard, no switching between tools.', icon: 'activity-icon.svg' },
    { title: 'Built for Growing Teams', text: 'Scale from a small operation to a multi-role business smoothly.', icon: 'integration-icon.svg' },
    { title: 'Customer-First Experience', text: 'Deliver real-time tracking and faster fulfillment every day.', icon: 'order-tracking-icon.svg' }
];

const heroAudienceSegments = [
    {
        title: 'For retailers',
        text: 'Manage inventory, orders, and storefront operations from one unified dashboard.',
        tag: 'LIVE'
    },
    {
        title: 'For growing businesses',
        text: 'Coordinate teams, warehouse flow, and expense tracking without operational bottlenecks.'
    },
    {
        title: 'For operations teams',
        text: 'Execute faster with role clarity, real-time visibility, and invoice-ready workflows.'
    }
];

const getPromoSlides = (isMobile) => {

    const promoSlides = [
        {
            image: 'banners/desktop/1.jpg',//'placeholder-banner-1.svg',
            //title: 'Sell Faster. Fulfill Smarter.',
            //subtitle: 'Dummy slide image — replace anytime from public/images'
        },
        {
            image: 'banners/desktop/2.jpg',
            //title: 'One Dashboard. Total Control.',
            //subtitle: 'Dummy slide image — replace anytime from public/images'
        },
        {
            image: 'banners/desktop/3.jpg',
            //title: 'From Inventory to Invoice, Connected.',
            //subtitle: 'Dummy slide image — replace anytime from public/images'
        },
        {
            image: 'banners/desktop/16.jpg',
            //title: 'Turn Operations Into Growth.',
            //subtitle: 'Dummy slide image — replace anytime from public/images'
        }
    ];

    return isMobile ? promoSlides.map(slide => ({ ...slide, image: slide.image.replace('desktop/', 'mobile/Mobile_') })) : promoSlides;
}

const getSpotlightSlides = (isMobile) => {

    const promoSlides = [
        {
            image: 'banners/desktop/7.jpg',//'placeholder-banner-1.svg',
            //title: 'Sell Faster. Fulfill Smarter.',
            //subtitle: 'Dummy slide image — replace anytime from public/images'
        },
        {
            image: 'banners/desktop/8.jpg',
            //title: 'One Dashboard. Total Control.',
            //subtitle: 'Dummy slide image — replace anytime from public/images'
        },
        {
            image: 'banners/desktop/9.jpg',
            //title: 'From Inventory to Invoice, Connected.',
            //subtitle: 'Dummy slide image — replace anytime from public/images'
        }
    ];

    return isMobile ? promoSlides.map(slide => ({ ...slide, image: slide.image.replace('desktop/', 'mobile/Mobile_') })) : promoSlides;
}

const secondaryPromoSlides = [
    {
        image: 'placeholder-banner-4.svg',
        title: 'Your Brand Story Belongs Here',
        subtitle: 'Placeholder slide — replace with real campaign visual'
    },
    {
        image: 'placeholder-banner-2.svg',
        title: 'Showcase New Offers and Events',
        subtitle: 'Placeholder slide — replace with real campaign visual'
    },
    {
        image: 'placeholder-banner-1.svg',
        title: 'Promote What Matters Most',
        subtitle: 'Placeholder slide — replace with real campaign visual'
    }
];

const peopleSpotlights = [
    {
        image: 'placeholder-people-1.svg',
        phrase: 'Happy teams close more orders.'
    },
    {
        image: 'placeholder-people-2.svg',
        phrase: 'Confident operations build loyal customers.'
    },
    {
        image: 'placeholder-people-3.svg',
        phrase: 'Better visibility. Better business days.'
    }
];

const operationalFlow = [
    {
        title: 'Control Inventory With Confidence',
        text: "Know what you have in stock, what's running low, and what's selling, so you can make better decisions every day.",
        image: 'inventory-page-odanow.png'
    },
    {
        title: 'Process Orders Without Bottlenecks',
        text: 'Automate order handling and tracking updates so every customer knows exactly what is happening.',
        image: 'orders-page-odanow.png'
    },
    {
        title: 'Keep Team and Warehouse in Sync',
        text: 'Coordinate people and tasks across fulfillment workflows while maintaining full operational clarity.',
        image: 'team-page-odanow.png'
    }
];

const businessOutcomes = [
    {
        title: 'Reduce Operational Stress',
        description: 'Simplify your daily processes by managing everything in one place instead of juggling multiple tools.'
    },
    {
        title: 'Protect Margins With Better Tracking',
        description: 'Keep a close eye on your orders, inventory, and expenses to avoid losses and improve profitability.'
    },
    {
        title: 'Make Better Business Decisions',
        description: 'Get clear insights into your operations so you can plan smarter and grow with confidence.'
    }
];

const trustPoints = [
    'Built for retailers, distributors, and fast-moving businesses',
    'Simple and easy to use, even for non-technical teams',
    'Grows with your operations without adding complexity',
    'Clear visibility across sales, warehouse, and finances'
];

const boldHighlights = [
    { metric: '7 Core Modules', label: 'Inventory, storefront, warehouse, team, orders, expenses, invoices' },
    { metric: '1 Unified Dashboard', label: 'One place to run and monitor daily operations' },
    { metric: 'Real-Time Visibility', label: 'Live tracking for stock movement and order progress' },
    { metric: 'Faster Execution', label: 'Reduce delays with connected operational workflows' }
];

const useCaseSegments = [
    {
        title: 'Retail Stores',
        text: 'Keep products available, process orders quickly, and deliver a smooth customer buying experience.'
    },
    {
        title: 'Distributors & Wholesalers',
        text: 'Coordinate warehouse dispatch, team activities, and invoice cycles without losing visibility.'
    },
    {
        title: 'D2C Brands',
        text: 'Launch your branded storefront with custom domain and manage fulfillment in one platform.'
    },
    {
        title: 'Growth-Stage Businesses',
        text: 'Scale operations confidently with a system designed to handle increasing complexity.'
    }
];

const onboardingSteps = [
    {
        step: '01',
        title: 'Set Up Your Storefront',
        text: 'Create your branded online store and get your business ready to receive orders. ',
        icon: 'collaborate-icon.svg',
        caption: 'Brand setup and launch readiness'
    },
    {
        step: '02',
        title: 'Organize Inventory and Warehouse',
        text: ' Structure your products and stock so everything is easy to manage and track.',
        icon: 'activity-icon.svg',
        caption: 'Operational flow and team alignment'
    },
    {
        step: '03',
        title: 'Go Live and Scale',
        text: 'Start selling, fulfill orders smoothly, and grow your business without the usual stress.',
        icon: 'chart-icon.svg',
        caption: 'Data-driven growth and retention'
    }
];

const faqItems = [
    {
        question: 'Can I use my own domain for my online store?',
        answer: 'Yes, OdaNow supports custom storefront setup so your business can run on your own branded domain.'
    },
    {
        question: 'How does OdaNow support my warehouse and team workflows?',
        answer: 'OdaNow helps you manage your warehouse activities and keep your team aligned with simple tools that improve coordination and day-to-day operations.'
    },
    {
        question: 'How does expense tracking work?',
        answer: 'You can easily record expenses and attach supporting files like receipts, so your records stay clear, organized, and accountable.'
    },
    {
        question: 'Can invoices be generated directly on the platform?',
        answer: 'Yes, invoice generation is built in, so you can create and send invoices quickly without extra steps.'
    },
    {
        question: 'Can I manage both online and offline inventory from one place?',
        answer: 'Yes, OdaNow gives you a single view of your inventory, making it easy to track stock across all your sales channels.'
    },
    {
        question: 'Is order tracking visible to customers?',
        answer: 'Yes, customers receive clear updates on their orders, which builds trust and reduces the need for follow-ups.'
    },
    {
        question: 'Can different team members have different responsibilities?',
        answer: 'Yes, you can assign roles and responsibilities so each team member knows what to handle, improving accountability and workflow.'
    },
    {
        question: 'Will this replace spreadsheets and manual reconciliation?',
        answer: 'Yes, OdaNow helps you bring everything into one system, reducing the need for manual tracking and repetitive tasks.'
    },
    {
        question: 'Is order management suitable for small businesses starting out?',
        answer: "Yes, it's simple enough for small teams to start with and flexible enough to support your growth over time."
    },
    {
        question: 'Can I keep records of expenses with proof documents?',
        answer: 'Yes, you can attach documents to expense entries, making your records easy to verify and manage.'
    },
    {
        question: 'How quickly can my team start using OdaNow?',
        answer: 'Most teams can get started quickly by setting up their storefront, inventory, and workflows step by step.'
    }
];

const PricingViewItem = ({ text, note }) => (
    <div className='modern-price-feature-item'>
        <span className='modern-check-icon'></span>
        <span className='modern-price-feature-copy'>
            <span className='modern-price-feature-text'>{text}</span>
            {note ? <span className='modern-price-feature-note'>{note}</span> : null}
        </span>
    </div>
);

const formatNaira = (amount) => `N${amount.toLocaleString('en-NG')}`;

const FaqItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={`modern-faq-card modern-faq-item${isOpen ? ' is-open' : ''}`}>
            <button
                type='button'
                className='modern-faq-trigger'
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
            >
                <span style={{ ...h2Style, fontSize: 16, padding: 0 }}>{question}</span>
                <span className='modern-faq-icon' aria-hidden='true'>+</span>
            </button>

            {isOpen ? (
                <div className='modern-faq-answer'>
                    <div style={{ ...p1Style, marginTop: 0 }}>{answer}</div>
                </div>
            ) : null}
        </div>
    );
};

const pricingPlans = [
    {
        name: 'Starter Plan',
        accent: '#2f5e8f',
        summary: 'Perfect for small businesses taking their first step into digital order and inventory management.',
        monthlyAmount: 10000,
        discountedMonthlyAmount: 8500,
        features: [
            { text: 'Products / Inventory Capacity: Up to 100 items', note: 'Bulk upload and export supported' },
            { text: 'Monthly Orders & Invoices: Up to 250', note: 'Includes bulk export' },
            { text: 'File Storage: 5GB (images & attachments)' },
            { text: 'Online Storefront: Included', note: 'Secure customer login portal' },
            { text: 'Team Access: 1 user + Admin', note: 'Support bulk download of reports' },
            { text: 'Vendors / Suppliers: Up to 15' },
            { text: 'Warehouse Management: Single warehouse support' },
            { text: 'Customer Records: Up to 500', note: 'Bulk download & export enabled' },
            { text: 'Expense Tracking: Up to 50 entries / month', note: 'Bulk export supported' },
            { text: 'Analytics Dashboard: Included' },
            { text: 'Support: Email support' }
        ],
        contactLine: null,
        ctaLabel: 'Get Started',
        ctaLink: 'https://web.odanow.net'
    },
    {
        name: 'Premium Plan',
        accent: '#7ab24f',
        summary: 'Best for rapidly growing retail and distribution businesses with structured teams.',
        monthlyAmount: 30000,
        discountedMonthlyAmount: 25000,
        features: [
            { text: 'Products / Inventory Capacity: Up to 600 items', note: 'Bulk upload / export included' },
            { text: 'Monthly Orders & Invoices: Up to 10,000', note: 'Invoice payment links included' },
            { text: 'File Storage: 20GB' },
            { text: 'Online Storefront: Included (+ branding options)' },
            { text: 'Team Access: 3 users + Admin' },
            { text: 'Customers: Up to 5,000' },
            { text: 'Vendors / Suppliers: Up to 500' },
            { text: 'Warehouse Management: Up to 3 warehouses' },
            { text: 'Expense Tracking: 500 entries / month' },
            { text: 'Barcode Generation: From SKU or Part Number' },
            { text: 'Low Stock Alerts: Automated inventory notifications' },
            { text: 'MOQ (Minimum Order Quantity): Settings available' },
            { text: 'Flexible Discounts: Custom percentage discounts' },
            { text: 'Reporting & Audits:', note: 'Includes tax & compliance reporting (Coming Soon)' },
            { text: 'Support: Priority email + chat support' }
        ],
        contactLine: null,
        ctaLabel: 'Get Started',
        ctaLink: 'https://web.odanow.net'
    },
    {
        name: 'Enterprise Plan',
        accent: '#e0782f',
        summary: 'Best for rapidly growing retail and distribution businesses with structured teams.',
        monthlyAmount: null,
        discountedMonthlyAmount: null,
        features: [
            { text: 'Unlimited Products / Inventory' },
            { text: 'Unlimited Monthly Orders & Invoices' },
            { text: 'Unlimited Customers & Vendors' },
            { text: 'Warehouse Management: Multi-warehouse + Bin/Location tracking' },
            { text: 'Team Access: Unlimited users with role-based access control (RBAC)' },
            { text: 'Full Audit Trails & Compliance Reporting' },
            { text: 'Custom Dashboard & BI Reporting' },
            { text: 'Dedicated Account Manager + 24/7 Support' },
            { text: 'Dedicated onboarding & training' },
            { text: 'Custom integrations (POS, ERP, Accounting systems)' }
        ],
        contactLine: 'Contact us: info@odanowteam.net',
        ctaLabel: 'Contact Us',
        ctaLink: 'mailto:info@odanowteam.net'
    }
];

const CapabilityCard = ({ title, description, icon }) => (
    <div className='modern-card modern-hover-lift'>
        <div className='modern-card-icon' style={{ backgroundImage: icon ? `url('images/${icon}')` : null }}></div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={h2Style}>{title}</span>
            <span style={p1Style}>{description}</span>
        </div>
    </div>
);

const PricingBoxView = ({ name, accent, summary, monthlyAmount, discountedMonthlyAmount, features, contactLine, ctaLabel, ctaLink }) => {
    const yearlyAmount = typeof discountedMonthlyAmount === 'number' ? discountedMonthlyAmount * 12 : null;

    return (
        <div className='modern-price-card'>
            <div className='modern-price-head' style={{ borderTopColor: accent }}>
                <span className='modern-price-name'>{name}</span>
                <span className='modern-price-summary'>{summary}</span>

                {typeof monthlyAmount === 'number' ? (
                    <div className='modern-price-lines'>
                        <span className='modern-price-line'>
                            <span className='modern-price-amount'>{formatNaira(monthlyAmount)}</span>{' '}
                            <span className='modern-price-unit'>per month</span>
                        </span>
                        {typeof discountedMonthlyAmount === 'number' ? (
                            <>
                                <span className='modern-price-or-line'>
                                    <span className='modern-price-or'>OR</span>
                                </span>
                                <span className='modern-price-line modern-price-line-secondary'>
                                <span className='modern-price-amount'>{formatNaira(yearlyAmount)}</span>{' '}
                                <span className='modern-price-unit'>per year</span>
                                <span className='modern-price-sub'> ({formatNaira(discountedMonthlyAmount)} per month)</span>
                                </span>
                            </>
                        ) : null}
                    </div>
                ) : null}

                {contactLine ? <span className='modern-price-contact'>{contactLine}</span> : null}

                <div className='modern-price-cta-wrap'>
                    <a className='modern-btn primary full modern-price-cta' href={ctaLink}>{ctaLabel}</a>
                </div>
            </div>
            <div className='modern-price-body'>
                {features.map((feature) => (
                    <PricingViewItem key={feature.text} text={feature.text} note={feature.note} />
                ))}
            </div>
        </div>
    );
};

const FooterContactLink = ({ href, label, icon, isImage }) => (
    <a
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: 'white',
            textDecoration: 'none',
            fontSize: 13,
            padding: '4px 10px',
            borderRadius: 16,
            background: 'rgba(255,255,255,0.14)'
        }}
    >
        {isImage ? (
            <img src={icon} alt='' style={{ width: 14, height: 14 }} />
        ) : (
            <span
                style={{
                    width: 16,
                    height: 16,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.22)',
                    fontSize: 11,
                    fontWeight: 'bold'
                }}
            >
                {icon}
            </span>
        )}
        <span>{label}</span>
    </a>
);

const HomePage = () => {
    const { isMobile } = useMobileDetect();

    return (
        <div style={{ width: '100%' }}>
            <ScrollToAnchor />

            <section id='home' className='modern-container modern-hero modern-hero-boost'>
                <div className='modern-hero-text modern-hero-copy'>
                    <span className='modern-hero-eyebrow'>OPERATIONS INFRASTRUCTURE FOR MODERN BUSINESS</span>

                    <h1 className='modern-hero-title'>
                        The first unified<br />
                        Inventory <span>&amp;</span> Order<br />
                        Infrastructure for Teams <span>&amp;</span> Growth
                    </h1>

                    <p className='modern-hero-subtext'>
                        Storefronts, warehouse flow, team coordination, and billing controls in one
                        <span> integrated stack</span>. Practical, production-ready, and built to scale with your daily operations.
                    </p>

                    <div className='modern-hero-segments'>
                        {heroAudienceSegments.map((segment) => (
                            <div key={segment.title} className='modern-hero-segment'>
                                <div className='modern-hero-segment-head'>
                                    <span className='modern-hero-segment-title'>{segment.title}</span>
                                    {segment.tag ? <span className='modern-hero-segment-tag'>{segment.tag}</span> : null}
                                </div>
                                <span className='modern-hero-segment-text'>{segment.text}</span>
                            </div>
                        ))}
                    </div>

                    <div className='modern-action-row'>
                        <Link className='modern-btn primary' to={'https://web.odanow.net'}>
                            Start Now
                        </Link>
                        <Link className='modern-btn secondary' to={'#features'}>
                            Explore Features
                        </Link>
                    </div>
                </div>

                <div className='modern-hero-visual-wrap'>
                    <div className='modern-hero-image modern-hero-image-boost' style={{ backgroundImage: "url('images/banner-image-01.png')" }}></div>
                    <div className='modern-hero-float-card modern-hover-lift'>
                        <span className='modern-hero-float-label'>OPS MODE</span>
                        <span className='modern-hero-float-title'>Launch in 3 clear steps</span>
                        <span className='modern-hero-float-text'>Set up your store, organize inventory, and go live with confidence.</span>
                    </div>
                </div>
            </section>

            <section className='modern-container modern-promo-slider-wrap modern-section-shell'>
                <BannerSlider slides={getPromoSlides(isMobile)} autoPlayMs={4500} />
            </section>

            <section className='modern-container modern-grid3 modern-section-shell'>
                {quickHighlights.map((item) => (
                    <div key={item.title} className='modern-highlight-card modern-hover-lift'>
                        <div className='modern-small-icon' style={{ backgroundImage: `url('images/${item.icon}')` }}></div>
                        <div>
                            <div style={{ ...h2Style, padding: 0, fontSize: 15 }}>{item.title}</div>
                            <div style={{ ...p1Style, marginTop: 2, fontSize: 13 }}>{item.text}</div>
                        </div>
                    </div>
                ))}
            </section>

            <section className='modern-container modern-section-shell'>
                <div className='modern-bold-strip modern-metrics-strip'>
                    {boldHighlights.map((item) => (
                        <div key={item.metric} className='modern-bold-item modern-metric-card modern-hover-lift'>
                            <div className='modern-metric-title'>{item.metric}</div>
                            <div className='modern-metric-label'>{item.label}</div>
                            <a className='modern-metric-action' href='https://web.odanow.net'>Explore ↗</a>
                        </div>
                    ))}
                </div>
            </section>

            <section className='modern-container modern-intro-copy modern-section-shell'>
                <h2 style={{ ...h1Style, textAlign: 'center' }}>
                    Turn Daily Business Complexity Into Smooth, Repeatable Workflows
                </h2>
                <p style={{ ...p1Style, textAlign: 'center', maxWidth: 900, margin: '0 auto' }}>
                    Managing your business across different apps and manual processes can slow you down.
                </p>
                <p style={{ ...p1Style, textAlign: 'center', maxWidth: 900, margin: '0 auto' }}>
                    <span style={{ fontStyle: 'italic', color: '#333333' }}>Oda</span><span style={{ fontStyle: 'italic', color: '#ef5a29' }}>Now</span> helps you bring everything into one smooth system so your team can work better together, avoid mistakes, and get more done in less time.
                </p>
            </section>

            <section id='features' className='modern-container modern-section-shell modern-usecase-section'>
                <div className='modern-section-header'>
                    <span style={{ ...h1Style, textAlign: 'center' }}>
                        Everything You Need to Scale Faster with <span style={{ fontStyle: 'italic', color: '#333333' }}>Oda</span>
                        <span style={{ fontStyle: 'italic', color: '#ef5a29' }}>Now</span>
                    </span>
                    <span style={{ ...p1Style, textAlign: 'center' }}>
                        All the tools you need to manage your business, grow your sales, and stay in control without the usual stress.
                    </span>
                </div>

                <div className='modern-capability-grid'>
                    {platformCapabilities.map((capability) => (
                        <CapabilityCard
                            key={capability.title}
                            title={capability.title}
                            description={capability.description}
                            icon={capability.icon}
                        />
                    ))}
                </div>
            </section>

            <section className='modern-surface'>
                <div className='modern-section-header'>
                    <img src={isMobile ? 'images/banners/mobile/Mobile_6.jpg' : 'images/banners/desktop/6.jpg'} alt='faq' style={{ display: 'block', borderRadius: 10, maxWidth: '100%' }} />
                </div>
                {/*<div className='modern-container'>
                    <div style={{ ...h1Style, textAlign: 'center', fontSize: isMobile ? 23 : 25 }}>See How Operations Come Together</div>
                    <div style={{ ...p1Style, textAlign: 'center', maxWidth: 800, margin: '0 auto 22px' }}>
                        Keep your products, orders, team, and finances connected in one place so nothing gets lost or delayed.
                    </div>

                    <div className='modern-flow-grid'>
                        {operationalFlow.map((flow) => (
                            <div key={flow.title} className='modern-flow-card modern-hover-lift'>
                                <div className='modern-flow-image' style={{ backgroundImage: `url('images/${flow.image}')` }}></div>
                                <div style={{ padding: 14 }}>
                                    <div style={{ ...h2Style, paddingTop: 0 }}>{flow.title}</div>
                                    <div style={{ ...p1Style, marginTop: 4 }}>{flow.text}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>*/}
            </section>

            <section className='modern-container modern-section-shell'>
                {/* <div className='modern-people-grid'>
                    {peopleSpotlights.map((item) => (
                        <div
                            key={item.image}
                            className='modern-people-card modern-hover-lift'
                            style={{ backgroundImage: `url('images/${item.image}')` }}
                        >
                            <div className='modern-people-phrase'>{item.phrase}</div>
                        </div>
                    ))}
                </div> */}

                <section className='modern-container modern-promo-slider-wrap modern-section-shell'>
                    <BannerSlider slides={getSpotlightSlides(isMobile)} autoPlayMs={4500} />
                </section>
                <div className='modern-section-header'>
                    <span style={{ ...h1Style, textAlign: 'center' }}>What You Gain With <span style={{ color: '#454545' }}>Oda</span><span style={{ color: '#f18423' }}>Now</span></span>
                    <span style={{ ...p1Style, textAlign: 'center', maxWidth: 760 }}>
                        OdaNow is not just software. It is a practical operating system for businesses that want to grow without
                        creating unnecessary complexity.
                    </span>
                </div>
                <div className='modern-grid2'>
                    {businessOutcomes.map((outcome) => (
                        <div key={outcome.title} className='modern-outcome-card modern-hover-lift'>
                            <div style={{ ...h2Style, color: '#ef5a29' }}>{outcome.title}</div>
                            <div style={p1Style}>{outcome.description}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className='modern-container modern-section-shell'>
                <div className='modern-section-header'>
                    <span style={{ ...h1Style, textAlign: 'center' }}>Built for Businesses That Need Speed and Control</span>
                    <span style={{ ...p1Style, textAlign: 'center', maxWidth: 820 }}>
                        Great for businesses where staying organized, moving fast, and delivering on time really matters.
                    </span>
                </div>
                <div className='modern-segment-grid'>
                    {useCaseSegments.map((segment) => (
                        <div key={segment.title} className='modern-segment-card modern-hover-lift'>
                            <div style={{ ...h2Style, color: '#ef5a29' }}>{segment.title}</div>
                            <div style={p1Style}>{segment.text}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className='modern-container modern-section-shell'>
                <div className='modern-section-header'>
                    <span style={{ ...h1Style, textAlign: 'center' }}>Go Live in 3 Clear Steps</span>
                    <span style={{ ...p1Style, textAlign: 'center', maxWidth: 760 }}>
                        Start quickly, stay organized, and scale with confidence using a practical onboarding path.
                    </span>
                </div>
                <div className='modern-step-grid'>
                    {onboardingSteps.map((item, index) => (
                        <div key={item.step} className={`modern-step-card ${index === onboardingSteps.length - 1 ? 'is-last' : ''}`}>
                            <div className='modern-step-head'>
                                <div className='modern-step-icon' style={{ backgroundImage: `url('images/${item.icon}')` }}></div>
                                <div className='modern-step-number'>{item.step}</div>
                            </div>
                            <div style={{ ...h2Style, marginTop: 2 }}>{item.title}</div>
                            <div style={p1Style}>{item.text}</div>
                            <div className='modern-step-caption'>{item.caption}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/*<section className='modern-container'>
                <div className='modern-proof-wrap'>
                    <div>
                        <div style={{ ...h2Style, fontSize: 22, paddingTop: 0 }}>Why Businesses Choose <span style={{ color: '#454545' }}>Oda</span><span style={{ color: '#f18423' }}>Now</span></div>
                        <p style={{ ...p1Style, marginTop: 4 }}>
                            OdaNow is built to remove the daily stress that slows teams down.
                        </p>
                        <p style={{ ...p1Style, marginTop: 4 }}>
                            Instead of switching between spreadsheets, chats, and different tools, everything works together in one simple system so your business stays organized and your team stays aligned.
                        </p>
                        <p style={{ ...p1Style, marginTop: 4 }}>
                            This means fewer delays, more accurate orders, cleaner records, and a smoother experience for your customers from start to finish.
                        </p>
                    </div>
                    <div className='modern-proof-list'>
                        {trustPoints.map((point) => (
                            <div key={point} className='modern-proof-item'>
                                <span className='modern-check-icon'></span>
                                <span style={{ ...p1Style, margin: 0 }}>{point}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* <section className='modern-container modern-promo-slider-wrap'>
                <BannerSlider slides={secondaryPromoSlides} autoPlayMs={5200} />
            </section> */}


            <section className='modern-container modern-section-shell'>
                <div className='modern-section-header'>
                    <img src={isMobile ? 'images/banners/mobile/Mobile_13.jpg' : 'images/banners/desktop/13.jpg'} alt='faq' style={{ display: 'block', borderRadius: 10, maxWidth: '100%' }} />
                </div>
            </section>

            <section className='modern-container modern-section-shell'>
                <div className='modern-section-header'>
                    <img src={isMobile ? 'images/banners/mobile/Mobile_14.jpg' : 'images/banners/desktop/14.jpg'} alt='faq' style={{ display: 'block', borderRadius: 10, maxWidth: '100%' }} />
                </div>
                <div className='modern-faq-grid'>
                    {faqItems.map((item) => (
                        <FaqItem key={item.question} question={item.question} answer={item.answer} />
                    ))}
                </div>
            </section>

            <section className='modern-container modern-section-shell'>
                <div className='modern-section-header' id='pricing-plans'>
                    <span style={{ ...h1Style, textAlign: 'center' }}>Flexible Plan for Ambitious Businesses</span>
                    <span style={{ ...p1Style, textAlign: 'center' }}>
                        Start with what you need today and grow into more as your business expands.
                    </span>
                </div>
                <div className='modern-pricing-wrap'>
                    {pricingPlans.map((plan) => (
                        <PricingBoxView key={plan.name} {...plan} />
                    ))}
                </div>
            </section>

            <section className='modern-footer-cta'>
                <div className='modern-footer-cta-inner'>
                    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 480 }}>
                        <span style={{ ...h1Style, color: 'white' }}>
                            Ready to simplify operations and accelerate growth?
                        </span>
                        <span style={{ ...p1Style, color: 'white' }}>
                            Bring inventory, storefront, warehouse, team management, orders, expenses, and invoicing into one
                            smart workflow with OdaNow.
                        </span>
                        <div style={{ padding: '15px 0px' }}>
                            <Link className='modern-btn light' to={'https://web.odanow.net'}>
                                Launch Your Store
                            </Link>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <FooterContactLink href='tel:+2347041139782' label='+234 704 113 9782' icon={'\u260E'} />
                            <FooterContactLink href='mailto:info@odanowteam.net' label='info@odanowteam.net' icon={'\u2709'} />
                            <FooterContactLink
                                href='https://instagram.com/odanowapp'
                                label='@odanowapp'
                                icon='/social/instagram.svg'
                                isImage={true}
                            />
                            <FooterContactLink
                                href='https://www.linkedin.com/company/odanow'
                                label='OdaNow'
                                icon='in'
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <img className='modern-footer-image' src={'images/footer-banner-image.png'} alt='OdaNow business growth banner' />
                    </div>
                </div>
            </section>

            {/* <div
                style={{
                    display: 'flex',
                    position: 'relative',
                    width: '100%',
                    minHeight: 20,
                    padding: 0,
                    alignItems: 'center',
                    background: '#ef5a29',
                    justifyContent: 'center'
                }}
            ></div> */}
        </div>
    );
};

export default LayoutWrapper(HomePage, { showNavigationBar: true, showSideMenu: true });
